import { is } from 'bpmn-js/lib/util/ModelUtil';
import { isSafeImageUrl } from '../utils/sanitize';
import { getOpenLightbox } from '../utils/bridge';

// --- Helper functions (module scope) ---

function getDocumentation(element) {
  const bo = element.businessObject;
  if (bo?.documentation?.length > 0) {
    return bo.documentation[0].text || '';
  }
  return '';
}

function getImageUrl(element) {
  const bo = element.businessObject;
  if (bo && is(element, 'bpmn:Task')) {
    return bo.get('custom:imageUrl') || '';
  }
  return '';
}

function getVideoTimestamp(element) {
  const bo = element.businessObject;
  if (bo && is(element, 'bpmn:Task')) {
    return bo.get('custom:videoTimestamp') || '';
  }
  return '';
}

function formatTimestamp(seconds) {
  const s = Number.parseFloat(seconds);
  if (Number.isNaN(s)) return seconds;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/**
 * Shows a hover tooltip on BPMN elements in View mode.
 * Displays enlarged image (custom:imageUrl) and/or documentation (bpmn:documentation).
 * Only active when the viewer is running (no editing).
 */
export default function HoverTooltipProvider(eventBus, overlays, elementRegistry) {
  let currentOverlayId = null;
  let currentElementId = null;
  let hideTimeout = null;

  function createTooltipHtml(imageUrl, docText, elementName, videoTs) {
    const container = document.createElement('div');
    container.className = 'hover-tooltip';

    // Prevent mouse events on tooltip from triggering element.out
    container.addEventListener('mouseenter', function () {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    });
    container.addEventListener('mouseleave', function () {
      removeTooltip();
    });

    // Element name as title
    if (elementName) {
      const title = document.createElement('div');
      title.className = 'hover-tooltip-title';
      title.textContent = elementName;
      container.appendChild(title);
    }

    // Image (click opens lightbox) — only safe data: URLs
    if (imageUrl && isSafeImageUrl(imageUrl)) {
      const imgWrapper = document.createElement('div');
      imgWrapper.className = 'hover-tooltip-image';
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = 'Element-Bild – Klicken zum Vergrößern';
      img.title = 'Klicken zum Vergrößern';
      img.addEventListener('click', function (e) {
        e.stopPropagation();
        const openLightbox = getOpenLightbox();
        if (openLightbox) openLightbox(imageUrl, null);
      });
      img.onerror = function () {
        imgWrapper.style.display = 'none';
      };
      imgWrapper.appendChild(img);
      container.appendChild(imgWrapper);
    }

    // Video timestamp
    if (videoTs) {
      const tsDiv = document.createElement('div');
      tsDiv.className = 'hover-tooltip-doc';
      tsDiv.style.color = '#0dcaf0';
      tsDiv.style.fontWeight = '600';
      tsDiv.textContent = '⏱ Video: ' + formatTimestamp(videoTs);
      container.appendChild(tsDiv);
    }

    // Documentation text
    if (docText) {
      const docDiv = document.createElement('div');
      docDiv.className = 'hover-tooltip-doc';
      docDiv.textContent = docText;
      container.appendChild(docDiv);
    }

    return container;
  }

  function removeTooltip() {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    if (currentOverlayId !== null) {
      try {
        overlays.remove(currentOverlayId);
      } catch {
        // overlay may already be removed
      }
      currentOverlayId = null;
      currentElementId = null;
    }
  }

  function showTooltip(element) {
    // Don't show tooltip for the root element or connections
    if (!element?.businessObject) return;
    if (element.type === 'bpmn:Process' || element.type === 'bpmn:Collaboration') return;
    if (element.waypoints) return; // Skip connections/sequence flows

    const imageUrl = getImageUrl(element);
    const docText = getDocumentation(element);
    const videoTs = getVideoTimestamp(element);
    const elementName = element.businessObject.name || '';

    // Only show if there's something to display
    if (!imageUrl && !docText && !videoTs) return;

    // Don't re-show for same element
    if (currentElementId === element.id) return;

    // Remove previous tooltip
    removeTooltip();

    const html = createTooltipHtml(imageUrl, docText, elementName, videoTs);

    try {
      currentOverlayId = overlays.add(element, 'hover-tooltip', {
        position: {
          bottom: -10,
          left: 0
        },
        html: html,
        show: {
          minZoom: 0.2
        },
        scale: {
          min: 1,
          max: 1
        }
      });
      currentElementId = element.id;
    } catch (error_) {
      console.warn('Could not add hover tooltip:', error_);
    }
  }

  // Listen for hover events
  eventBus.on('element.hover', function (event) {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }

    const hoveredId = event.element.id;

    // If we have a tooltip for a different element, schedule removal
    if (currentElementId && hoveredId !== currentElementId) {
      hideTimeout = setTimeout(function () {
        removeTooltip();
        // After cleanup, try showing tooltip for the new element
        showTooltip(event.element);
      }, 150);
      return;
    }

    showTooltip(event.element);
  });

  eventBus.on('element.out', function (event) {
    // Only react if the element being left is the one with the tooltip
    if (event.element.id === currentElementId) {
      hideTimeout = setTimeout(function () {
        removeTooltip();
      }, 200);
    }
  });
}

HoverTooltipProvider.$inject = ['eventBus', 'overlays', 'elementRegistry'];
